import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./UtilitySetupPage.module.scss";
import Badge from "../../../common/components/xepp/badge/Badge";
import Button from "../../../common/components/xepp/button/Button";
import BpsDataTable, { BpsColumn } from "../../../common/components/bps-admin/data-table/BpsDataTable";
import { BpsActionMenu } from "../../../common/components/bps-admin/action-menu/BpsActionMenu";
import ConfirmationModal from "../../../common/components/xepp/confirmation-modal/ConfirmationModal";
import EmptyState from "../../../common/components/xepp/empty-state/EmptyState";
import { ArrowUpright, CheckmarkIcon, DeactivateIcon, EditIcon } from "../../../assets/icons";
import { UtilityConfiguration, UtilityStatus } from "../../../model/bps-admin/utility-configuration";
import {
	useGetUtilityConfigurationsQuery,
	useUpdateUtilityConfigurationStatusMutation,
} from "../../../service/utility-configuration-service";
import { getErrorMessage } from "../../../utils/error-handler";
import { ApplicationRoutes } from "../../../routes";

const PAGE_SIZE = 10;

const statusLabel: Record<UtilityStatus, string> = {
	[UtilityStatus.Active]: "Active",
	[UtilityStatus.Inactive]: "Inactive",
};

const UtilitySetupPage = () => {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [pendingStatusChange, setPendingStatusChange] = useState<UtilityConfiguration | null>(null);
	const navigate = useNavigate();

	const { data, isFetching } = useGetUtilityConfigurationsQuery({ page, pageSize: PAGE_SIZE, search });
	const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateUtilityConfigurationStatusMutation();

	const handleSearch = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const openUtility = (utility: UtilityConfiguration) => {
		window.open(`https://${utility.subDomain}`, "_blank", "noopener,noreferrer");
	};

	const editUtility = (utility: UtilityConfiguration) => {
		navigate(`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.BPS_UTILITY_SETUP}/${utility.id}`);
	};

	const confirmStatusChange = async () => {
		if (!pendingStatusChange) return;

		const isActivating = pendingStatusChange.status === UtilityStatus.Inactive;
		try {
			await updateStatus({ id: pendingStatusChange.id, isActive: isActivating }).unwrap();
			toast.success(`${pendingStatusChange.utilityName} ${isActivating ? "activated" : "deactivated"}`);
			setPendingStatusChange(null);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data) ?? "Could not update the utility status");
		}
	};

	// Built here rather than at module scope because the action menu closes over
	// component state.
	const columns: BpsColumn<UtilityConfiguration>[] = useMemo(
		() => [
			{
				header: "",
				accessor: "logo",
				width: 72,
				render: utility => (
					<div className={styles.logoCell}>
						{utility.logo && <img className={styles.logo} src={utility.logo} alt={utility.acronym} />}
					</div>
				),
			},
			{ header: "NAME", accessor: "utilityName" },
			{
				header: "SUBDOMAIN",
				accessor: "subDomain",
				render: utility => (
					<span className={styles.subDomain} title={utility.subDomain}>
						{utility.subDomain}
					</span>
				),
			},
			{
				header: "STATUS",
				accessor: "status",
				width: 120,
				render: utility => (
					<Badge variant={utility.status === UtilityStatus.Active ? "active" : "inactive"}>
						{statusLabel[utility.status]}
					</Badge>
				),
			},
			{
				header: "",
				accessor: "actions",
				width: 56,
				render: utility => (
					<BpsActionMenu
						items={[
							{ label: "Open Utility", icon: <ArrowUpright />, onClick: () => openUtility(utility) },
							{ label: "Edit Utility", icon: <EditIcon />, onClick: () => editUtility(utility) },
							// Deactivating is the destructive one; activating reads as a normal action.
							utility.status === UtilityStatus.Active
								? {
										label: "Deactivate Utility",
										icon: <DeactivateIcon />,
										danger: true,
										onClick: () => setPendingStatusChange(utility),
									}
								: {
										label: "Activate Utility",
										icon: <CheckmarkIcon />,
										onClick: () => setPendingStatusChange(utility),
									},
						]}
					/>
				),
			},
		],
		[],
	);

	const isActivating = pendingStatusChange?.status === UtilityStatus.Inactive;

	return (
		<>
			<BpsDataTable<UtilityConfiguration>
				title="Utility Setup"
				headerAction={
					<Button
						text="New Utility"
						onClick={() =>
							navigate(`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.BPS_UTILITY_SETUP_NEW}`)
						}
					/>
				}
				searchPlaceholder="Search Utility"
				onSearch={handleSearch}
				columns={columns}
				data={data?.items ?? []}
				loading={isFetching}
				emptyComponent={
					<EmptyState title="No utilities found" description="Utilities you create will show up here." />
				}
				page={page}
				pageSize={PAGE_SIZE}
				totalItems={data?.count ?? 0}
				onPageChange={setPage}
			/>

			<ConfirmationModal
				isOpen={!!pendingStatusChange}
				title={isActivating ? "Activate Utility" : "Deactivate Utility"}
				description={isActivating ? "Confirm activation" : "Confirm deactivation"}
				message={`Are you sure you want to ${isActivating ? "activate" : "deactivate"} this utility?`}
				confirmText="Yes"
				cancelText="No"
				// "danger" renders an outlined red button; the design calls for the filled brand one.
				variant="info"
				isLoading={isUpdatingStatus}
				onConfirm={confirmStatusChange}
				onClose={() => setPendingStatusChange(null)}
			/>
		</>
	);
};

export default UtilitySetupPage;
